import { useCallback, useEffect, useRef, useState } from 'react';
import type { Dispatch, RefObject, SetStateAction } from 'react';
import type { CorrectionLevel } from '@orborix/shared-types';
import type { UseVoiceSessionReturn } from '@orborix/voice-client';
import {
  getConversationPrefs,
  getEnglishState,
  saveConversationPrefs,
} from '../../../lib/englishApi.js';
import type { ScenarioStartContext } from '../../scenarios/scenarioTypes.js';
import { sessionIntensity } from '../sessionIntensity.js';
import {
  CONV_DEFAULTS,
  clamp,
  modeToSessionIo,
  normalizeConvMode,
  SPEED_MAX,
  SPEED_MIN,
  type ConvSettings,
} from '../conversationTypes.js';
import { prepareReviewOpenings } from '../assisted/reviewVoice.js';
import { LIVE_PA_AVAILABLE } from '../livePronunciation.js';

/** Velocidad recomendada mostrada antes de que `/state` resuelva la real (natural). */
const SPEED_DEFAULT_REC = 1;

/**
 * Prefs apagadas sólo para ESTA sesión (ver `restoreRef`): el examen apaga
 * `correction`/`phonetic`; el build nativo apaga `phonetic` (el análisis fonético
 * en vivo es sólo web — `livePronunciation.ts`).
 */
type SessionOffRestore = Partial<Pick<ConvSettings, 'correction' | 'phonetic'>>;

/** Lo que se guarda como pref: los valores reales de lo apagado sólo para la sesión. */
function toPersisted(
  s: ConvSettings,
  restore: SessionOffRestore | null,
  scenarioIntensity: ConvSettings['intensity'] | null,
): ConvSettings {
  return { ...s, ...restore, ...(scenarioIntensity ? { intensity: scenarioIntensity } : {}) };
}

export interface UseConversationSettingsReturn {
  /** Settings de la sesión (estado de UI sembrado de prefs persistidas). */
  settings: ConvSettings;
  setSettings: Dispatch<SetStateAction<ConvSettings>>;
  /** Intención global explícita desde Ajustes, incluso si coincide con la escena. */
  setGlobalIntensity: (intensity: ConvSettings['intensity']) => void;
  /** Sensibilidad de correcciones EFECTIVA (auto→CEFR / manual). Filtra el display. */
  correctionLevelEff: CorrectionLevel;
  /** Sensibilidad recomendada por CEFR (server-resolved) — guía para el SettingsSheet. */
  correctionRec: CorrectionLevel;
  /** Velocidad recomendada por nivel EFECTIVO (server-resolved) — valor del modo auto. */
  speedRec: number;
  /** Velocidad EFECTIVA (pref explícita ?? recomendada por nivel) — la que se usa/muestra. */
  speedEff: number;
  /** Prefs de DB hidratadas → destraba el handshake WS con valores correctos. */
  prefsReady: boolean;
}

/**
 * Settings de la conversación: estado de UI + hidratación desde la DB +
 * persistencia debounced + aplicación EN VIVO a la sesión (diffing). La VOZ no
 * se maneja acá (`useCoachPicker` la persiste y aplica en caliente); el VOLUMEN
 * tampoco (device-local en localStorage, a propósito).
 *
 * `sessionRef` lo llena el controller en el mismo render en que crea la sesión;
 * los effects de acá corren después, así que siempre la ven poblada.
 */
export function useConversationSettings(args: {
  scenarioCtx: ScenarioStartContext | null;
  examMode: boolean;
  sessionRef: RefObject<UseVoiceSessionReturn | null>;
}): UseConversationSettingsReturn {
  const { scenarioCtx, examMode, sessionRef } = args;

  // ─── Session settings (UI state, seeded from defaults + DB) ───────────
  // El viejo seed de `micMode` desde localStorage (`useCaptureMode`) murió con
  // el composer compartido: la sesión es siempre `captureMode: 'hold'`. El
  // campo sigue en el contrato de prefs (DB) pero ya no se lee.
  const [settings, updateSettings] = useState<ConvSettings>(() => ({
    ...CONV_DEFAULTS,
    // `speed` es tri-state (null = auto por nivel, plan 38 F1). La fuente de
    // verdad es la pref de DB (se hidrata abajo) — localStorage no puede
    // representar null, así que el seed arranca en auto y la hidratación manda.
    speed: null,
    // La dificultad elegida en el briefing override-a la intensidad para ESTA
    // sesión (no se persiste como pref — ver proyección persistible más abajo).
    ...(scenarioCtx ? { intensity: scenarioCtx.intensity } : {}),
    ...(LIVE_PA_AVAILABLE ? {} : { phonetic: false }),
    ...(examMode ? { correction: false, phonetic: false } : {}),
  }));

  // Sensibilidad de correcciones recomendada por CEFR (Fase 14B). La trae el
  // `/state` (server-resolved, canónica). El nivel EFECTIVO se computa acá
  // combinándola con el toggle auto/manual de `settings` — reactivo a cambios
  // en vivo del SettingsSheet sin duplicar el mapa CEFR→nivel en el cliente.
  const [correctionRec, setCorrectionRec] = useState<CorrectionLevel>('balanced');
  const correctionLevelEff: CorrectionLevel = settings.correctionAuto
    ? correctionRec
    : settings.correctionLevel;

  // Velocidad recomendada por nivel EFECTIVO (server-resolved, `/state`). La
  // efectiva se deriva con la pref tri-state (`speed ?? recomendada`), mismo
  // patrón que la sensibilidad de correcciones — sin duplicar el mapa CEFR→valor.
  const [speedRec, setSpeedRec] = useState<number>(SPEED_DEFAULT_REC);
  const speedEff: number = settings.speed ?? speedRec;

  const [prefsReady, setPrefsReady] = useState(false);

  // ─── Settings → live session updates ──────────────────────────────────
  const prev = useRef<ConvSettings>(settings);
  useEffect(() => {
    const p = prev.current;
    const s = sessionRef.current;
    if (!s) return;
    // Toggle de voz del coach (header): muta el OUTPUT en vivo vía el
    // `mode.switch` existente. Al mutear mid-turno el backend cancela el TTS
    // remoto (deja de gastar caracteres); el audio ya bufferizado se drena.
    if (p.mode !== settings.mode) s.setIoMode(modeToSessionIo(settings.mode));
    if (p.translation !== settings.translation) s.setTranslationEnabled(settings.translation, 'es');
    if (p.speed !== settings.speed) {
      // Tri-state (plan 38 F1): con la pref en auto (null) aplicamos la velocidad
      // efectiva por nivel (`speedRec` de `/state`). El server ya arrancó en la
      // resuelta (handshake); esto sólo cubre cambios mid-sesión.
      const rate = clamp(settings.speed ?? speedRec, SPEED_MIN, SPEED_MAX);
      s.setSpeechRate(rate);
    }
    if (
      p.intensity !== settings.intensity ||
      p.correction !== settings.correction ||
      p.phonetic !== settings.phonetic ||
      p.voiceCorrections !== settings.voiceCorrections
    ) {
      s.sendSessionUpdate({
        intensity: settings.intensity,
        correction: settings.correction,
        phonetic: settings.phonetic,
        // `phonetic` también ENCHUFA/DESENCHUFA el motor de PA: el backend
        // (buildWsEventHandlers) hace setPronunciationProvider(null) cuando es
        // false → deja de llamar a phonos. Debe ser boolean (no string): el
        // handler exige `typeof paPatch === 'boolean'`.
        pronunciationAssessment: settings.phonetic,
        // Coda hablada (manos libres): mandamos el EFECTIVO — apagar el master
        // de correcciones también apaga la coda. Boolean, mismo criterio que PA.
        voiceCorrections: settings.correction && settings.voiceCorrections,
      });
    }
    // Búsqueda en internet: aplica EN VIVO al ruteo del LLM (el backend flipa
    // el LlmProcessor; surte efecto en el próximo turno). Update dedicado y
    // boolean estricto — el handler del backend exige `typeof === 'boolean'`.
    if (p.webSearch !== settings.webSearch) {
      s.sendSessionUpdate({ webSearch: settings.webSearch });
    }
    // Estilo de conversación (registro / región / tono). Update dedicado para no
    // arrastrar `phonetic`/`pronunciationAssessment` (que enchufan/desenchufan el
    // motor de PA). El runtime muta su estado y el próximo turno del coach
    // re-bakea el prompt con el nuevo estilo — sin cortar el turno en curso.
    if (
      p.register !== settings.register ||
      p.region !== settings.region ||
      p.tone !== settings.tone
    ) {
      s.sendSessionUpdate({
        register: settings.register,
        region: settings.region,
        tone: settings.tone,
      });
    }
    // "Español del profe": sólo la elección EXPLÍCITA viaja (con pref null el
    // server ya arrancó con la resuelta por país). Aplica desde el próximo
    // reply (prompt + acento de la voz), como el tono.
    if (p.spanishVariant !== settings.spanishVariant && settings.spanishVariant !== null) {
      s.sendSessionUpdate({ spanishVariant: settings.spanishVariant });
    }
    prev.current = settings;
  }, [settings, speedRec, sessionRef]);

  // ─── Persisted conversation prefs (DB, per-user) ──────────────────────
  // Nunca escribimos un snapshot completo sin haber leído las prefs originales.
  // Si el GET falla, la sesión puede arrancar; se reintenta y se superponen los
  // cambios intencionales pendientes, sin guardar defaults inventados.
  const [prefsLoaded, setPrefsLoaded] = useState(false);
  const loadedRef = useRef(false);
  const startContextRef = useRef({ scenarioCtx, examMode });
  const currentSettingsRef = useRef(settings);
  const pendingRef = useRef<Partial<ConvSettings>>({});
  const globalIntensityRef = useRef<ConvSettings['intensity'] | null>(null);
  const scenarioIntensityRef = useRef<ConvSettings['intensity'] | null>(null);
  const restoreRef = useRef<SessionOffRestore | null>(null);
  const lastPersistedRef = useRef<string | null>(null);
  const writesRef = useRef<Promise<void>>(Promise.resolve());
  const [retry, setRetry] = useState(0);

  const setSettings: Dispatch<SetStateAction<ConvSettings>> = useCallback((action) => {
    const current = currentSettingsRef.current;
    const next = typeof action === 'function' ? action(current) : action;
    if (!loadedRef.current) {
      const changed = Object.fromEntries(
        Object.entries(next).filter(([key, value]) => current[key as keyof ConvSettings] !== value),
      );
      pendingRef.current = { ...pendingRef.current, ...changed };
    }
    currentSettingsRef.current = next;
    updateSettings(next);
  }, []);

  const setGlobalIntensity = useCallback(
    (intensity: ConvSettings['intensity']) => {
      // Una elección igual al override sigue siendo una elección GLOBAL real.
      globalIntensityRef.current = intensity;
      scenarioIntensityRef.current = intensity;
      setSettings((current) => ({ ...current, intensity }));
    },
    [setSettings],
  );

  useEffect(() => {
    let cancelled = false;
    let recoveryTimer: ReturnType<typeof setTimeout> | undefined;
    const { scenarioCtx, examMode } = startContextRef.current;
    const levelPromise = getEnglishState().catch(() => null);
    const load = async () => {
      try {
        const [prefs, levelState] = await Promise.all([getConversationPrefs(), levelPromise]);
        if (cancelled) return;
        if (levelState) {
          setCorrectionRec(levelState.recommendedCorrectionLevel);
          setSpeedRec(levelState.recommendedSpeed);
        }
        scenarioIntensityRef.current = globalIntensityRef.current ?? prefs.intensity;
        if (examMode) {
          restoreRef.current = { correction: prefs.correction, phonetic: prefs.phonetic };
        } else if (!LIVE_PA_AVAILABLE) {
          restoreRef.current = { phonetic: prefs.phonetic };
        }
        const base: ConvSettings = {
          ...prefs,
          mode: normalizeConvMode(prefs.mode),
          // La intensidad con la que el backend VA a correr la sesión: bajo
          // overlay de exploración ignora el hint (también el de la escena) y
          // usa la efectiva del /state — ver `sessionIntensity`.
          intensity: sessionIntensity(
            scenarioCtx ? scenarioCtx.intensity : (levelState?.effectiveIntensity ?? prefs.intensity),
            levelState,
          ),
          ...(LIVE_PA_AVAILABLE ? {} : { phonetic: false }),
          ...(examMode ? { correction: false, phonetic: false } : {}),
        };
        // Dedup del GET, sin incluir cambios pendientes: esos deben guardarse.
        lastPersistedRef.current = JSON.stringify(
          toPersisted(base, restoreRef.current, scenarioCtx ? prefs.intensity : null),
        );
        const next = {
          ...base,
          ...pendingRef.current,
          ...(globalIntensityRef.current ? { intensity: globalIntensityRef.current } : {}),
        };
        currentSettingsRef.current = next;
        updateSettings(next);
        pendingRef.current = {};
        loadedRef.current = true;
        setPrefsLoaded(true);
      } catch {
        if (!cancelled) recoveryTimer = setTimeout(() => void load(), 1500);
      } finally {
        if (!cancelled) setPrefsReady(true); // el fallo de prefs no bloquea la sesión
      }
    };
    void load();
    return () => {
      cancelled = true;
      clearTimeout(recoveryTimer);
    };
    // Contexto de inicio congelado; la recuperación pertenece al mismo montaje.
  }, []);

  useEffect(() => {
    if (!prefsLoaded) return;
    const toSave = toPersisted(
      settings,
      restoreRef.current,
      startContextRef.current.scenarioCtx ? scenarioIntensityRef.current : null,
    );
    const serialized = JSON.stringify(toSave);
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    const saveTimer = setTimeout(() => {
      // Serializar los PUT evita que una respuesta vieja gane a una elección nueva.
      writesRef.current = writesRef.current.then(async () => {
        if (cancelled || serialized === lastPersistedRef.current) return;
        try {
          await saveConversationPrefs(toSave);
          lastPersistedRef.current = serialized; // sólo un PUT confirmado deduplica
          // El tono, la variedad o el nivel pueden haber cambiado la voz del coach: la primera
          // línea del repaso queda lista con la de ahora (card #595).
          prepareReviewOpenings();
        } catch {
          if (!cancelled) retryTimer = setTimeout(() => setRetry((n) => n + 1), 1500);
        }
      });
    }, 600);
    return () => {
      cancelled = true;
      clearTimeout(saveTimer);
      clearTimeout(retryTimer);
    };
  }, [settings, prefsLoaded, retry]);

  return {
    settings,
    setSettings,
    setGlobalIntensity,
    correctionLevelEff,
    correctionRec,
    speedRec,
    speedEff,
    prefsReady,
  };
}
